import type {
	CSSProperties,
	JSX,
	MouseEvent,
	PointerEvent,
	ReactElement,
	KeyboardEvent as ReactKeyboardEvent,
	ReactNode,
	Ref,
} from 'react';
import { Children, cloneElement, isValidElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ComputePositionConfig, OffsetOptions, Placement, Strategy } from '@floating-ui/dom';
import { arrow, computePosition, flip, offset as floatOffset, shift } from '@floating-ui/dom';
import { cx } from 'classix';
import { LazyMotion, m } from 'framer-motion';

import { FocusTrap } from '@launchpad-ui/focus-trap';
import { Overlay } from '@launchpad-ui/overlay';

import styles from './styles/Popover.module.css';

const loadFeatures = () =>
	import(
		/* webpackChunkName: "lp-popover-framer-features" */
		/* webpackExports: "domAnimation" */
		'framer-motion'
	).then((res) => res.domAnimation);

type Offset = OffsetOptions;

/**
 * Minimal shape for the caller-supplied `target` element. The Popover clones
 * arbitrary target elements (any host component or custom component the
 * consumer renders), reading `disabled` for styling and injecting a DOM ref
 * plus a couple of data/aria attributes. We can't know the target's real
 * prop type without knowing what the consumer rendered, so this narrows just
 * far enough to type-check the specific reads/writes below.
 */
type PopoverTargetElementProps = {
	disabled?: boolean;
	ref?: Ref<Element>;
	'aria-describedby'?: string;
	'data-state'?: 'open' | 'closed';
};

type PopoverProps = {
	allowBoundaryElementOverflow?: boolean;
	content?: string | JSX.Element | JSX.Element[];
	children: ReactNode;
	disabled?: boolean;
	disablePlacementFlip?: boolean;
	enforceFocus?: boolean;
	hoverCloseDelay?: number;
	hoverOpenDelay?: number;
	interactionKind?: 'hover' | 'hover-target-only' | 'hover-or-focus' | 'click';
	isFixed?: boolean;
	isModal?: boolean;
	isOpen?: boolean;
	offset?: Offset;
	onClick?(): void;
	onClose?(event?: Event): void;
	onInteraction?(isOpen: boolean): void;
	placement?: Placement;
	popoverClassName?: string;
	popoverContentClassName?: string;
	restrictHeight?: boolean;
	restrictWidth?: boolean;
	rootElementStyle?: CSSProperties;
	rootElementTag?: keyof JSX.IntrinsicElements;
	target?: string | JSX.Element;
	targetElementRef?: Ref<Element>;
	targetClassName?: string;
	targetTestId?: string;
	enableArrow?: boolean;
	'data-test-id'?: string;
};

type PopoverContentProps = {
	onMouseEnter?: (event: MouseEvent) => void;
	onMouseLeave?: (event: MouseEvent) => void;
	onPointerEnter?: (event: PointerEvent) => void;
	onPointerLeave?: (event: PointerEvent) => void;
	onClick?: (event: MouseEvent) => void;
};

const isOrContainsElement = (referenceElement: Element, element: Element) => {
	return referenceElement === element || referenceElement?.contains(element);
};

function mergeElementRefs<T>(...refs: Array<Ref<T> | undefined>) {
	return (node: T | null) => {
		const cleanups: Array<() => void> = [];
		const detach: Array<Ref<T>> = [];
		for (const elementRef of refs) {
			if (elementRef === undefined || elementRef === null) {
				continue;
			}
			if (typeof elementRef === 'function') {
				const cleanup = elementRef(node);
				if (typeof cleanup === 'function') {
					cleanups.push(cleanup);
				} else {
					detach.push(elementRef);
				}
			} else {
				const refObject = elementRef;
				// oxlint-disable-next-line no-param-reassign -- forward the instance onto the caller's ref
				refObject.current = node;
				detach.push(elementRef);
			}
		}
		if (cleanups.length === 0) {
			return;
		}
		return () => {
			for (const cleanup of cleanups) {
				cleanup();
			}
			for (const elementRef of detach) {
				if (typeof elementRef === 'function') {
					elementRef(null);
					continue;
				}
				if (elementRef === null) {
					continue;
				}
				const refObject = elementRef;
				// oxlint-disable-next-line no-param-reassign -- clear the object ref React will not detach itself
				refObject.current = null;
			}
		};
	};
}

/**
 * @deprecated use `Popover` from `@launchpad-ui/components` instead
 *
 * https://launchpad.launchdarkly.com/?path=/docs/components-overlays-popover--docs
 */
const Popover = ({
	rootElementTag = 'span',
	placement = 'bottom',
	restrictHeight = true,
	restrictWidth = true,
	isModal = false,
	isFixed = false,
	interactionKind = 'click',
	hoverOpenDelay = 250,
	hoverCloseDelay = 250,
	disablePlacementFlip = false,
	allowBoundaryElementOverflow = false,
	isOpen: isOpenProp,
	enableArrow,
	enforceFocus,
	onClick,
	onInteraction,
	onClose,
	disabled,
	children,
	target: targetProp,
	content: contentProp,
	targetClassName,
	targetTestId,
	popoverClassName,
	popoverContentClassName,
	rootElementStyle,
	offset,
	targetElementRef,
	'data-test-id': testId = 'popover',
}: PopoverProps) => {
	const [isOpen, setIsOpen] = useState(isOpenProp ?? undefined);
	const [openProp, setOpenProp] = useState(isOpenProp);
	if (isOpenProp !== openProp) {
		setOpenProp(isOpenProp);
		setIsOpen(isOpenProp);
	}
	const [popoverElement, setPopoverElement] = useState<HTMLElement | null>();
	const popoverElementRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		popoverElementRef.current = popoverElement ?? null;
	}, [popoverElement]);

	const targetRef = useRef<HTMLElement>(null);
	const contentRef = useCallback((node: HTMLElement | null) => {
		if (node !== null) {
			return setPopoverElement(node);
		}
		return;
	}, []);
	const arrowRef = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const optionsRef = useRef<Partial<ComputePositionConfig>>({});
	const popoverId = `popover-${useId()}`;

	const updatePosition = useCallback(async () => {
		const middleware = [];
		const popoverNode = popoverElementRef.current;

		if (popoverNode === null) {
			return;
		}

		if (!allowBoundaryElementOverflow) {
			middleware.push(shift({ padding: 5 }));
		}

		if (!disablePlacementFlip && !offset) {
			middleware.push(flip({ padding: 5 }));
		}

		if (offset) {
			middleware.push(floatOffset(offset));
		}

		if (enableArrow && arrowRef.current) {
			middleware.push(arrow({ element: arrowRef.current }));
		}

		const hasModal = targetRef.current?.closest('.has-overlay');
		const strategy: Strategy = isFixed || hasModal ? 'fixed' : 'absolute';

		optionsRef.current = {
			placement,
			middleware,
			strategy,
		};

		const parentNode = targetRef.current;
		if (!parentNode || !parentNode.childNodes) {
			return;
		}

		const target = parentNode.childNodes[0] as Element;
		const {
			x,
			y,
			placement: floatPlacement,
			middlewareData,
			strategy: floatStrategy,
		} = await computePosition(target, popoverNode, optionsRef.current);

		const positionedNode = popoverElementRef.current;
		if (positionedNode) {
			Object.assign(positionedNode.style, {
				left: `${x}px`,
				top: `${y}px`,
				position: floatStrategy,
			});

			positionedNode.setAttribute('data-popover-placement', floatPlacement);
		}

		if (enableArrow && arrowRef.current && middlewareData.arrow) {
			const { x: arrowX, y: arrowY } = middlewareData.arrow;

			const staticSide = {
				top: 'bottom',
				right: 'left',
				bottom: 'top',
				left: 'right',
			}[floatPlacement.split('-')[0]];

			if (staticSide) {
				Object.assign(arrowRef.current?.style, {
					left: arrowX !== null ? `${arrowX}px` : '',
					top: arrowY !== null ? `${arrowY}px` : '',
					right: '',
					bottom: '',
					[staticSide]: '5px',
				});
			}
		}
	}, [allowBoundaryElementOverflow, disablePlacementFlip, enableArrow, isFixed, offset, placement]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const updatePopover = async () => {
			if (isOpen && !(popoverElement === null || popoverElement === undefined)) {
				window.addEventListener('scroll', updatePosition, { passive: true });
				window.addEventListener('resize', updatePosition, { passive: true });
				if (contentProp !== undefined) {
					await updatePosition();
				} else {
					await updatePosition();
				}
			} else {
				window.removeEventListener('scroll', updatePosition);
				window.removeEventListener('resize', updatePosition);
			}
		};
		void updatePopover();
	}, [isOpen, contentProp, popoverElement, updatePosition]);

	const handleTargetClick = (event: MouseEvent) => {
		const eventTarget = event.target as Element;
		onClick?.();
		if (!disabled && targetRef.current && isOrContainsElement(targetRef.current, eventTarget)) {
			// always close the menu, and only open unless something prevented the default
			setOpenState(isOpen ? false : !event.defaultPrevented);
		}
	};

	const handleMouseEnter = () => {
		if (!disabled) {
			setOpenState(true, hoverOpenDelay);
			attachGlobalListener();
		}
	};

	const handleMouseLeave = () => {
		setOpenState(false, hoverCloseDelay);
		removeGlobalListener();
	};

	const handleFocus = () => {
		if (!disabled) {
			setOpenState(true);
			attachGlobalListener();
		}
	};

	const handleBlur = () => {
		setOpenState(false);
		removeGlobalListener();
	};

	const handlePopoverClick = (event: MouseEvent) => {
		const eventTarget = event.target as Element;
		if (eventTarget?.closest?.('.popover-dismiss')) {
			setOpenState(false);
		}
	};

	const handleOverlayClose = (event: MouseEvent | ReactKeyboardEvent) => {
		const eventTarget = event.target as Element;
		if (
			(targetRef.current && !isOrContainsElement(targetRef.current, eventTarget)) ||
			event.nativeEvent instanceof KeyboardEvent
		) {
			setOpenState(false);
		}
	};

	const setOpenState = (nextIsOpen: boolean, timeout?: number) => {
		timeoutRef.current && clearTimeout(timeoutRef.current);

		if (typeof timeout !== 'undefined' && timeout > 0) {
			timeoutRef.current = setTimeout(() => setOpenState(nextIsOpen), timeout);
		} else {
			// controlled mode
			if (isOpenProp === null || isOpenProp === undefined) {
				setIsOpen(nextIsOpen);
			} else {
				typeof onInteraction === 'function' && onInteraction(nextIsOpen);
			}

			if (!nextIsOpen) {
				typeof onClose === 'function' && onClose();
			}
		}
	};

	const parseChildren = (): {
		target: ReactNode;
		content: ReactNode;
	} => {
		const [targetChild, contentChild] = Children.toArray(children);

		return {
			target: targetChild ?? targetProp,
			content: contentChild ?? contentProp,
		};
	};

	const attachGlobalListener = () => {
		window.addEventListener('keydown', handleKeyDown, true);
	};

	const removeGlobalListener = () => {
		window.removeEventListener('keydown', handleKeyDown, true);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			event.stopPropagation();
			setIsOpen(false);
			removeGlobalListener();
		}
	};

	const renderPopover = (content: ReactNode) => {
		const classes = cx(styles.Popover, popoverClassName);

		let handlers: PopoverContentProps = {};

		if (interactionKind === 'hover') {
			handlers = {
				onMouseEnter: handleMouseEnter,
				onMouseLeave: handleMouseLeave,
				onPointerEnter: handleMouseEnter,
				onPointerLeave: handleMouseLeave,
			};
		}

		if (interactionKind !== 'hover-target-only') {
			handlers.onClick = handlePopoverClick;
		}

		const popoverContent = (
			<LazyMotion strict features={loadFeatures}>
				<m.div
					transition={{ duration: 0.15 }}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					className={cx(
						styles['Popover-content'],
						restrictWidth && styles['Popover-content--restrictWidth'],
						popoverContentClassName,
					)}
					data-popover-content=""
					tabIndex={interactionKind === 'click' ? -1 : undefined}
				>
					{enableArrow && <div id="arrow" ref={arrowRef} />}
					{restrictHeight ? <div className={styles['Popover-scroller']}>{content}</div> : content}
				</m.div>
			</LazyMotion>
		);

		return (
			<div
				id={popoverId}
				ref={contentRef}
				className={classes}
				role="tooltip"
				data-test-id={testId}
				aria-hidden={!isOpen}
				{...handlers}
			>
				{enforceFocus || interactionKind === 'click' ? (
					<FocusTrap autoFocus>{popoverContent}</FocusTrap>
				) : (
					popoverContent
				)}
			</div>
		);
	};

	const { target, content } = parseChildren();
	const childRef = isValidElement(target)
		? (target.props as { ref?: Ref<HTMLElement | null> }).ref
		: undefined;
	const hasEmptyContent = content === null || content === undefined || (typeof content === 'string' && !content);
	const isTargetDisabled = isValidElement(target)
		? !!(target as ReactElement<PopoverTargetElementProps>)?.props?.disabled
		: false;

	const isHoverInteraction =
		interactionKind === 'hover' || interactionKind === 'hover-target-only' || interactionKind === 'hover-or-focus';

	const Root = rootElementTag as 'div';

	return (
		<Root
			ref={targetRef as Ref<HTMLDivElement>}
			className={cx(styles['Popover-target'], targetClassName, isTargetDisabled && styles['Popover-target--disabled'])}
			style={rootElementStyle}
			data-test-id={targetTestId || 'popover-target'}
			data-popover-target=""
			{...(isHoverInteraction
				? {
						onMouseEnter: handleMouseEnter,
						onMouseLeave: handleMouseLeave,
						onPointerEnter: handleMouseEnter,
						onPointerLeave: handleMouseLeave,
						...(interactionKind === 'hover-or-focus' ? { onFocus: handleFocus, onBlur: handleBlur } : {}),
					}
				: { onClick: handleTargetClick })}
		>
			{cloneElement(target as ReactElement<PopoverTargetElementProps>, {
				...(targetElementRef === undefined || targetElementRef === null
					? {}
					: {
							ref: mergeElementRefs(childRef, targetElementRef),
						}),
				...(isOpen && { 'aria-describedby': popoverId }),
				'data-state': isOpen ? 'open' : 'closed',
			})}
			<Overlay
				isOpen={!!isOpen && !hasEmptyContent}
				canOutsideClickClose={interactionKind === 'click'}
				isModal={isModal}
				enforceFocus={enforceFocus}
				onClose={handleOverlayClose}
			>
				<div>{renderPopover(content)}</div>
			</Overlay>
		</Root>
	);
};

export { Popover };
export type { Offset, PopoverProps };
